import ChartDataLabels from "chartjs-plugin-datalabels"
import { Pie } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js"
import { ProjectDashboardViewModel } from "../../api/project-dashboard"
import { Box, Typography } from "@material-ui/core"
import { keys } from "lodash"
import { getOrCreateTooltip } from "./util"
import LegendItem from "./LegendItem"
import { STATUS_COLORS } from "../../../constants"
import { Status } from "../../../types/common"
import { useTranslations } from "./useTranslations"

ChartJS.register(ArcElement, Tooltip, ChartDataLabels)

const useTasksPieChart = ({ projectDashboard }: TasksPieChartProps) => {
  const translations = useTranslations()
  const {
    totalNumberOfTasks,
    groupedNotStartedTasks,
    groupedInProgressTasks,
    groupedCompletedTasks,
    groupedRejectedTasks,
  } = projectDashboard

  const notStartedStatusAndSubStatus = keys(groupedNotStartedTasks)
  const notStartedColorVariations = STATUS_COLORS.NOT_STARTED
  const notStartedLegendData = notStartedStatusAndSubStatus.map((status, index) => {
    const { numberOfTasks: value, percentageOfTasks, tasks } = groupedNotStartedTasks[status]
    const label = status === Status.NOT_STARTED ? translations.notStarted : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = notStartedColorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfTasks?.toFixed(1)
    return { label, boxColor, value, percentage, tasks }
  })

  const inProgressStatusAndSubStatus = keys(groupedInProgressTasks)
  const inProgressCOlorVariations = STATUS_COLORS.IN_PROGRESS
  const inProgressLegendData = inProgressStatusAndSubStatus.map((status, index) => {
    const { numberOfTasks: value, percentageOfTasks, tasks } = groupedInProgressTasks[status]
    const label = status === Status.IN_PROGRESS ? translations.inProgress : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = inProgressCOlorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfTasks?.toFixed(1)
    return { label, boxColor, value, percentage, tasks }
  })

  const completedStatusAndSubStatus = keys(groupedCompletedTasks)
  const completedColorVariations = STATUS_COLORS.COMPLETED
  const completedLegendData = completedStatusAndSubStatus.map((status, index) => {
    const { numberOfTasks: value, percentageOfTasks, tasks } = groupedCompletedTasks[status]
    const label = status === Status.COMPLETED ? translations.completed : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = completedColorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfTasks?.toFixed(1)
    return { label, boxColor, value, percentage, tasks }
  })

  const rejectedStatusAndSubStatus = keys(groupedRejectedTasks)
  const rejectedColorVariations = STATUS_COLORS.REJECTED
  const rejectedLegendData = rejectedStatusAndSubStatus.map((status, index) => {
    const { numberOfTasks: value, percentageOfTasks, tasks } = groupedRejectedTasks[status]
    const label = status === Status.REJECTED ? translations.rejected : status
    const colorKey = index > 3 ? 3 : index
    const boxColor = rejectedColorVariations[colorKey.toString() as "0" | "1" | "2" | "3"]
    const percentage = percentageOfTasks?.toFixed(1)
    return { label, boxColor, value, percentage, tasks }
  })

  const combinedLegendData = [
    ...completedLegendData,
    ...inProgressLegendData,
    ...notStartedLegendData,
    ...rejectedLegendData,
  ]
  const combinedData = {
    labels: combinedLegendData.map((item) => item.label),
    datasets: [
      {
        label: "Tasks",
        data: combinedLegendData.map((item) => item.percentage),
        backgroundColor: combinedLegendData.map((item) => item.boxColor),
        borderColor: combinedLegendData.map((item) => item.boxColor),
        borderWidth: 1,
      },
    ],
  }

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        external: function (context: any) {
          const { chart, tooltip } = context
          let tooltipEl = getOrCreateTooltip(chart)

          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          const dataIndex = tooltip.dataPoints[0]?.dataIndex
          if (dataIndex !== undefined) {
            const { label, percentage, value, tasks } = combinedLegendData[dataIndex]
            let innerHtml = `<div style="font-weight: bold; margin-bottom: 4px;">${label}: ${percentage}% (${value})</div>`
            innerHtml += "<div>"
            tasks.forEach((task: string) => {
              innerHtml += `<div>• ${task}</div>`
            })
            innerHtml += "</div>"

            tooltipEl.innerHTML = innerHtml
          }

          // Position the tooltip
          const position = context.chart.canvas.getBoundingClientRect()
          tooltipEl.style.opacity = "1"
          tooltipEl.style.left = position.left + window.pageXOffset + tooltip.caretX + "px"
          tooltipEl.style.top = position.top + window.pageYOffset + tooltip.caretY + "px"
        },
      },
      datalabels: {
        formatter: (percentage: number) => {
          const value = Math.round((percentage / 100) * totalNumberOfTasks)
          return percentage > 5 ? `${percentage}%\n${value}` : ""
        },
        color: "#fff",
        font: {
          size: 14,
          weight: "bold" as const,
        },
        anchor: "center" as const,
        align: "end" as const,
      },
    },
  }

  const displayPieChart = totalNumberOfTasks > 0
  return { data: combinedData, options, displayPieChart, legendData: combinedLegendData, translations }
}

const TasksPieChart = ({ projectDashboard }: TasksPieChartProps) => {
  const { data, options, displayPieChart, legendData, translations } = useTasksPieChart({ projectDashboard })

  if (!displayPieChart) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }
  return (
    <Box display={"flex"} flexDirection="row" justifyContent="space-around" flexWrap={"wrap"}>
      <Box
        style={{
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <Pie data={data} options={options} />
      </Box>
      <Box>
        {legendData.map((item, index) => (
          <LegendItem
            key={index}
            label={item.label}
            color={item.boxColor}
            percentage={item.percentage}
            count={item.value}
          />
        ))}
      </Box>
    </Box>
  )
}

export default TasksPieChart
type TasksPieChartProps = {
  projectDashboard: ProjectDashboardViewModel
}
